import React from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { pathOr } from 'ramda';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl } from '../../../util/reactIntl';
import classNames from 'classnames';

import config from '../../../config';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import {
  convertUnitToSubUnit,
  convertMoneyToNumber,
  getCurrencySymbol,
  unitDivisor,
  getEstimatedPricePerBooking,
  formatMoney,
} from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { stringValuesToNumbers } from '../../../util/data';
import {
  Form,
  FieldStepperEditable,
  PrimaryButton,
  FieldSelect,
  Helper,
} from '../../../components';
import { withCustomerFee } from '../../../util/pricing';
import { CUSTOMER_COMMISSION_PERCENTAGE, PROVIDER_COMMISSION_PERCENTAGE } from '@swimmy_/helpers';

import css from './PricingForm.module.css';
import IconDown from "../../IconDown/IconDown";

const { Money } = sdkTypes;

export const PricingForm = (props) => {
  const {
    className,
    fetchErrors,
    intl,
    listing,
    onSubmit,
    saveActionMsg,
    updated,
    updateInProgress,
    averagePriceInformation,
  } = props;
  const { price } = listing.attributes;

  const unitDivisorValue = unitDivisor(config.currency);
  const handleSubmit = (values) => {
    const valuesAsNumber = stringValuesToNumbers(values);
    const { price } = valuesAsNumber;
    const priceWithSubUnit = convertUnitToSubUnit(price, unitDivisorValue);
    const { canShowInstantBooking } = values;
    const updatedValues = {
      price: new Money(priceWithSubUnit, config.currency),
      publicData: {
        instantBookingFrom: canShowInstantBooking === 'oui' ? 1 : undefined,
        minAmountPerHour: priceWithSubUnit,
        minimumHoursPerBooking: 1,
        pricingExtraHours: {
          vartiationType: 'reduction',
          variation: 50,
          fromNbHours: 3,
        },
      },
    };
    console.log('updatedValues', updatedValues);
    onSubmit(updatedValues);
  };

  const currencySymbol = getCurrencySymbol(config.currencyConfig);
  const minPriceValue = config.listingMinimumPriceSubUnits / unitDivisorValue;
  const { publishListingError, updateListingError, showListingsError } = fetchErrors || {};
  const hasError = publishListingError || updateListingError || showListingsError;
  const listingPublicData = pathOr({}, ['attributes', 'publicData'], listing);
  const { instantBookingFrom = 0 } = listingPublicData;
  const initialValues = {
    price: price ? `${convertMoneyToNumber(price).toString()}${currencySymbol}` : undefined,
    canShowInstantBooking: instantBookingFrom > 0 ? 'oui' : 'non',
  };
  const priceLabel = intl.formatMessage({ id: 'NewListingPricingPanel.PricingForm.priceLabel' });
  const minPriceErrorText = intl.formatMessage(
    { id: 'NewListingPricingPanel.PricingForm.minPriceError' },
    { amount: minPriceValue, currency: currencySymbol }
  );
  const priceValidators = validators.minNumberAsString(2, minPriceErrorText);
  const errorMessage = updateListingError
    ? intl.formatMessage({ id: 'NewListingPricingPanel.PricingForm.updateFailed' })
    : publishListingError
      ? intl.formatMessage({ id: 'Marketplace.publishListingFailed' })
      : intl.formatMessage({ id: 'Marketplace.showListingFailed' });
  const classes = classNames(css.root, className);
  const yes = intl.formatMessage({ id: 'Marketplace.yes' });
  const no = intl.formatMessage({ id: 'Marketplace.no' });
  const selectPlaceholder = intl.formatMessage({
    id: 'EditListingInformationForm.selectPlaceholder',
  });
  const instantBookingLabel = intl.formatMessage({ id: 'EditListingForm.instantBookingLabel' });
  const instantBookingInformation = intl.formatMessage({
    id: 'EditListingForm.instantBookingInformation',
  });
  const instantBookingDisclaimer = intl.formatMessage({
    id: 'EditListingForm.instantBookingDisclaimer',
  });
  const requiredMessage = intl.formatMessage({ id: 'Marketplace.requiredMessage' });

  return (
    <FinalForm
      {...props}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={(formRenderProps) => {
        const { disabled, ready, handleSubmit, invalid, pristine, values } = formRenderProps;
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;
        const valuesAsNumber = stringValuesToNumbers(values);
        const { price } = valuesAsNumber;
        const deptName = pathOr(
          undefined,
          ['attributes', 'privateData', 'location', 'addressDetails', 'dept'],
          listing
        );

        const priceAsMoney =
          typeof price === 'number' && !isNaN(price)
            ? new Money(convertUnitToSubUnit(price, unitDivisorValue), config.currency)
            : null;
        const guestPriceMoney = priceAsMoney ? withCustomerFee(priceAsMoney) : null;
        const hostEarningsAmount =
          priceAsMoney && typeof PROVIDER_COMMISSION_PERCENTAGE === 'number'
            ? Math.round((priceAsMoney.amount * (100 - PROVIDER_COMMISSION_PERCENTAGE)) / 100)
            : null;
        const hostEarningsMoney =
          hostEarningsAmount != null ? new Money(hostEarningsAmount, config.currency) : null;
        const isPriceValid = !!price && !invalid && !isNaN(price) && price >= minPriceValue;

        const getEstimatedRevenueText = () => {
          const priceQ1 = Math.round(averagePriceInformation.Q1 / 100);
          const priceQ3 = Math.round(averagePriceInformation.Q3 / 100);

          if (priceQ1 === priceQ3)
            return intl.formatMessage(
              { id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenueRangeEqualPart1' },
              { dept: deptName, price: priceQ1 }
            );

          return intl.formatMessage(
            { id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenueRangePart1' },
            { dept: deptName, priceQ1, priceQ3 }
          );
        };

        return (
          <Form onSubmit={handleSubmit} className={classes}>
            {hasError && <p className={css.error}>{errorMessage}</p>}
            <FieldStepperEditable
              name="price"
              initialValue={initialValues.price}
              label={priceLabel}
              min={minPriceValue}
              placeholder={`00${currencySymbol}`}
              unit={currencySymbol}
              validate={priceValidators}
              withButtons
            />
            {averagePriceInformation && !price && (
              <Helper
                className={css.helper}
                text={[
                  getEstimatedRevenueText(),
                  intl.formatMessage({
                    id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenueRangePart2',
                  }),
                  intl.formatMessage({
                    id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenueRangePart3',
                  }),
                  intl.formatMessage({
                    id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenueRangePart4',
                  }),
                  intl.formatMessage({
                    id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenueRangePart5',
                  }),
                ]}
              />
            )}
            {isPriceValid && guestPriceMoney && hostEarningsMoney && (
              <details className={css.priceBreakdown}>
                <summary className={css.priceBreakdownSummary}>
                  <span>
                    {intl.formatMessage(
                      { id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownSummary' },
                      {
                        price: intl.formatMessage(
                          { id: 'Marketplace.priceWithUnit' },
                          { price: formatMoney(intl, guestPriceMoney, false) }
                        ),
                      }
                    )}
                    <IconDown/>
                  </span>
                </summary>
                <div className={css.priceBreakdownContent}>
                  <div className={css.priceBreakdownSection}>
                    <div className={css.priceBreakdownSectionTitle}>
                      {intl.formatMessage(
                        {
                          id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownGuestTitle',
                        },
                        {
                          price: formatMoney(intl, guestPriceMoney, false),
                        }
                      )}
                    </div>
                    <ul className={css.priceBreakdownList}>
                      <li>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownBase',
                          },
                          {
                            price: formatMoney(intl, priceAsMoney, false),
                          }
                        )}
                      </li>
                      <li>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownCustomerFee',
                          },
                          {
                            price: formatMoney(
                              intl,
                              new Money(
                                guestPriceMoney.amount - priceAsMoney.amount,
                                config.currency
                              ),
                              false
                            ),
                            percentage: CUSTOMER_COMMISSION_PERCENTAGE,
                          }
                        )}
                      </li>
                    </ul>
                  </div>

                  <div className={css.priceBreakdownSection}>
                    <div className={css.priceBreakdownSectionTitle}>
                      {intl.formatMessage(
                        {
                          id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownHostTitle',
                        },
                        {
                          price: formatMoney(intl, hostEarningsMoney, false),
                        }
                      )}
                    </div>
                    <ul className={css.priceBreakdownList}>
                      <li>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownBase',
                          },
                          {
                            price: formatMoney(intl, priceAsMoney, false),
                          }
                        )}
                      </li>
                      <li>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownProviderFee',
                          },
                          {
                            price: formatMoney(
                              intl,
                              new Money(
                                priceAsMoney.amount - hostEarningsMoney.amount,
                                config.currency
                              ),
                              false
                            ),
                            percentage: PROVIDER_COMMISSION_PERCENTAGE,
                          }
                        )}
                      </li>
                    </ul>
                  </div>

                  <div className={css.priceBreakdownInfo}>
                    {intl.formatMessage({
                      id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownInfo',
                    })}{' '}
                    <a
                      href="https://help.swimmy.com/hc/fr/articles/360013461819-Quelle-est-la-commission-de-Swimmy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css.priceBreakdownLink}
                    >
                      {intl.formatMessage({
                        id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownLearnMore',
                      })}
                    </a>
                  </div>
                </div>
              </details>
            )}
            {getEstimatedPricePerBooking(price) && (
              <Helper
                className={css.helper}
                text={intl.formatMessage(
                  { id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenue' },
                  {
                    price: formatMoney(
                      intl,
                      new Money(getEstimatedPricePerBooking(price), config.currency),
                      true
                    ),
                  }
                )}
              />
            )}

            <FieldSelect
              className={css.instantBooking}
              id="canShowInstantBooking"
              name="canShowInstantBooking"
              label={instantBookingLabel}
              informationTooltipContent={instantBookingInformation}
              validate={validators.required(requiredMessage)}
            >
              <option value="">{selectPlaceholder}</option>
              <option value="oui">{yes}</option>
              <option value="non">{no}</option>
            </FieldSelect>
            <Helper className={css.helper} text={instantBookingDisclaimer} />
            <div className={css.buttonContainer}>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                {saveActionMsg}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

PricingForm.defaultProps = {
  fetchErrors: null,
  listing: null,
};

PricingForm.propTypes = {
  disabled: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  intl: intlShape.isRequired,
  listing: object,
  onSubmit: func.isRequired,
  ready: bool.isRequired,
  saveActionMsg: string.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
};

export default injectIntl(PricingForm);
